import { NamedColorPalette, SingularityBrandColor } from "@/types/singularity/brand";
import {
  getColorForBackground,
  getColorWithEnoughContrastFromBackgroundForText,
  getButtonColors,
} from "./logic";
import { ColorScheme, PossibleColorSchemes } from "./types";
import { sortSchemes } from "./utils";

/**
 * Generate all valid color schemes from a single palette
 * Each shade becomes a background, and helper functions compute contrasted colors
 *
 * @param palette The color palette to generate schemes from
 * @param type The palette type (primary, neutral, or secondary)
 * @param primaryPalette The primary palette to use for button colors (for neutral extremes)
 */
function generateSchemesFromPalette(
  palette: NamedColorPalette,
  type: 'primary' | 'neutral' | 'secondary',
  primaryPalette: NamedColorPalette
): ColorScheme[] {
  const schemes: ColorScheme[] = [];

  // Generate a scheme for each shade in the palette
  Object.values(palette.scale).forEach(bgColor => {
    // Use helper functions to compute all contrasted colors
    // Banned checks happen inside the helper functions
    const background = getColorForBackground(bgColor);
    const contrastedFromBackgroundForText = getColorWithEnoughContrastFromBackgroundForText(bgColor, palette);
    const buttonColors = getButtonColors(bgColor, palette, type, primaryPalette);
    const contrastedFromBackgroundForButton = buttonColors.bgNormal;
    const contrastedFromBackgroundForButtonHover = buttonColors.bgHover;
    const contrastedFromButtonForButtonText = buttonColors.textNormal;
    const contrastedFromButtonForButtonTextHover = buttonColors.textHover;

    schemes.push({
      background,
      contrastedFromBackgroundForText,
      contrastedFromBackgroundForButton,
      contrastedFromBackgroundForButtonHover,
      contrastedFromButtonForButtonText,
      contrastedFromButtonForButtonTextHover,
    });
  });

  return sortSchemes(schemes);
}

/**
 * Get all possible color schemes from brand colors
 *
 * @param brandColors - The brand colors from singularityStore.brand.color
 * @returns Object with schemes organized by palette type
 */
export function getPossibleColorSchemes(
  brandColors: SingularityBrandColor
): PossibleColorSchemes {
  const result: PossibleColorSchemes = {
    neutral: [],
    primary: [],
    secondary: []
  };

  if (!brandColors.primary) {
    return result;
  }

  if (brandColors.neutral) {
    result.neutral = generateSchemesFromPalette(brandColors.neutral, 'neutral', brandColors.primary);
  }

  result.primary = generateSchemesFromPalette(brandColors.primary, 'primary', brandColors.primary);

  if (brandColors.secondary && Array.isArray(brandColors.secondary)) {
    const primaryPalette = brandColors.primary; // Capture for closure
    result.secondary = brandColors.secondary.map(palette => {
      return generateSchemesFromPalette(palette, 'secondary', primaryPalette);
    });
  }

  return result;
}
